import { useApolloClient } from '@apollo/client/react';
import {
  Flexbox,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import { AvailabilityChartCard } from 'pages/Availability/common/AvailabilityChart/AvailabilityChartCard';
import { buildAvailabilityChartData } from 'pages/Availability/common/AvailabilityChart/utils/chartDataUtils';
import { CancelAndEndModal } from 'pages/Availability/common/Modals/CancelAndEndModal';
import { ReductionInfoModal } from 'pages/Availability/common/Modals/ReductionInfoModal';
import { ReductionsTableCard } from 'pages/Availability/common/ReductionsTable/ReductionsTableCard';
import {
  endOfDayInTimezone,
  getBaseAvailabilityEntriesFromSiteAvailabilities,
  getBaseAvailabilityEntryFromNewSiteAvailabilityEntry,
  PREVIEW_ENTRY_ID,
  startOfDayInTimezone,
} from 'pages/Availability/common/utils';
import { useSiteAvailabilities } from 'pages/Availability/SiteLevel/hooks';
import { useSiteAvailabilityFormDrawer } from 'pages/Availability/SiteLevel/useSiteAvailabilityFormDrawer';
import {
  AvailabilityChartData,
  BaseSiteAvailabilityEntry,
  Site,
} from 'pages/Availability/types';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'store';
import { TableActionType } from 'store/availability';

interface SiteLevelAvailabilityProps {
  site: Site;
  drawerWidth: string;
}

export const SiteLevelAvailability: React.FC<SiteLevelAvailabilityProps> = ({
  site,
  drawerWidth,
}) => {
  const {
    availabilityFormOpen,
    setAvailabilityFormOpen,
    persistedSiteAvailabilityFormData,
    availabilityDateRange,
    availabilityUserAction,
    setAvailabilityUserAction,
  } = useStore((state) => ({
    availabilityFormOpen: state.availabilityFormOpen,
    setAvailabilityFormOpen: state.setAvailabilityFormOpen,
    persistedSiteAvailabilityFormData: state.persistedSiteAvailabilityFormData,
    availabilityDateRange: state.availabilityDateRange,
    availabilityUserAction: state.availabilityUserAction,
    setAvailabilityUserAction: state.setAvailabilityUserAction,
  }));

  const client = useApolloClient();

  const [availabilityEntriesFromApi, setAvailabilityEntriesFromApi] = useState<
    BaseSiteAvailabilityEntry[]
  >([]);

  const [siteSourceName, siteSourceId] = [site.sourceName, site.sourceId];
  const siteSourceKey = getSourceKey(site);

  // Fetch site availabilities
  const { data: siteData, refetch: refetchSite } = useSiteAvailabilities({
    siteSourceName,
    siteSourceId,
    viewDateRange: {
      start: startOfDayInTimezone(availabilityDateRange.start, UK_TIMEZONE),
      end: endOfDayInTimezone(availabilityDateRange.end, UK_TIMEZONE),
    },
  });

  // Use the form drawer hook
  const { FormDrawer, previewEntries } = useSiteAvailabilityFormDrawer({
    site,
    siteSourceKey,
    client,
    title:
      availabilityUserAction.actionType === 'edit'
        ? `Edit reduction with ID ${availabilityUserAction.siteAvailabilityEntry?.id}`
        : 'Report a reduction',
    width: drawerWidth,
    editEntry: availabilityUserAction.siteAvailabilityEntry,
    onRefetch: refetchSite,
  });

  const hasPreviewEntries = previewEntries.length > 0;
  const viewDateRange = {
    start: hasPreviewEntries
      ? availabilityDateRange.start
      : startOfDayInTimezone(availabilityDateRange.start, UK_TIMEZONE),
    end: hasPreviewEntries
      ? availabilityDateRange.end
      : endOfDayInTimezone(availabilityDateRange.end, UK_TIMEZONE),
  };

  useEffect(() => {
    if (siteData?.siteAvailabilities) {
      setAvailabilityEntriesFromApi(
        getBaseAvailabilityEntriesFromSiteAvailabilities(
          siteData.siteAvailabilities,
        ),
      );
    }
  }, [siteData]);

  const availabilityEntries = useMemo<BaseSiteAvailabilityEntry[]>(() => {
    // Exclude the entry being edited to avoid duplication
    const filteredApi =
      availabilityUserAction.actionType === 'edit'
        ? availabilityEntriesFromApi.filter(
            (entry) =>
              entry.id !== availabilityUserAction.siteAvailabilityEntry!.id,
          )
        : availabilityEntriesFromApi;

    const hasPersistedDraft =
      availabilityFormOpen &&
      persistedSiteAvailabilityFormData &&
      Object.keys(persistedSiteAvailabilityFormData).length > 0;

    if (previewEntries.length > 0) {
      return [...previewEntries, ...filteredApi];
    }

    if (hasPersistedDraft) {
      return [
        getBaseAvailabilityEntryFromNewSiteAvailabilityEntry(
          PREVIEW_ENTRY_ID,
          persistedSiteAvailabilityFormData,
        ),
        ...filteredApi,
      ];
    }

    return filteredApi;
  }, [
    previewEntries,
    availabilityEntriesFromApi,
    availabilityFormOpen,
    persistedSiteAvailabilityFormData,
    availabilityUserAction.siteAvailabilityEntry,
  ]);

  const chartData = useMemo<AvailabilityChartData | null>(
    () =>
      buildAvailabilityChartData({
        availabilityControl: SiteAvailabilityControl.SiteLevel,
        availabilityEntries,
        availabilityFormOpen,
        viewDateRange,
        limits: site.siteLimits ?? [],
        assetNames: (site.assets ?? []).map((it) => it.name),
        previewEntries,
      }),
    [
      availabilityEntries,
      availabilityFormOpen,
      viewDateRange,
      site,
      previewEntries,
    ],
  );

  const handleEmptyAvailabilityUserAction = () => {
    setAvailabilityUserAction({
      siteAvailabilityEntry: null,
      assetAvailabilityEntry: null,
      actionType: null,
    });
  };

  return (
    <>
      <Flexbox
        flexDirection={'column'}
        alignItems={'center'}
        style={
          availabilityFormOpen
            ? { width: `calc(100% - ${drawerWidth})` }
            : { width: '100%' }
        }
      >
        <AvailabilityChartCard
          data={chartData}
          overlayMessage={
            availabilityFormOpen && previewEntries.length === 0
              ? 'Select a start and end time to generate this preview.'
              : null
          }
          timezone={UK_TIMEZONE}
        />
        <br />
        <Text prominence="subtle" size={Sizes.Small}>
          All times are shown in UK timezone
        </Text>
        <br />
        <ReductionsTableCard
          availabilityEntries={availabilityEntries}
          availabilityControl={SiteAvailabilityControl.SiteLevel}
          previewMode={availabilityFormOpen}
          timezone={UK_TIMEZONE}
        />
      </Flexbox>

      {FormDrawer}

      {availabilityUserAction.actionType === 'info' &&
        availabilityUserAction.siteAvailabilityEntry && (
          <ReductionInfoModal
            isOpen={availabilityUserAction.actionType === 'info'}
            setIsOpen={() => handleEmptyAvailabilityUserAction()}
            availabilityId={availabilityUserAction.siteAvailabilityEntry.id}
            availabilityControl={SiteAvailabilityControl.SiteLevel}
            timezone={UK_TIMEZONE}
          />
        )}

      {availabilityUserAction.siteAvailabilityEntry &&
        (availabilityUserAction.actionType === 'cancel' ||
          availabilityUserAction.actionType === 'end' ||
          availabilityUserAction.actionType === 'endAndStartNew') && (
          <CancelAndEndModal
            type={availabilityUserAction.actionType}
            onClose={(currentAction: TableActionType) => {
              handleEmptyAvailabilityUserAction();

              if (currentAction === 'endAndStartNew') {
                setAvailabilityFormOpen(true);
              }
              refetchSite();
            }}
            availabilityId={availabilityUserAction.siteAvailabilityEntry.id}
            availabilityControl={SiteAvailabilityControl.SiteLevel}
          />
        )}
    </>
  );
};
